import type { ReactNode } from 'react';
import { STRINGS } from '@/strings/strings';

/**
 * v3.1 §6 Schritt 1 · numbered-block progressive-reveal wrapper.
 *
 * Bau-Spez v3.1 §6 line 257 verbatim: "Abfolge nach G1. Jeder Block
 * erscheint erst, wenn der vorige beantwortet ist. Beim Wiedereinstieg
 * sind alle beantworteten Blöcke zugeklappt sichtbar."
 *
 * The primitive owns two rendered states — the parent decides visibility
 * of not-yet-reached blocks by NOT rendering them at all (progressive
 * reveal is a parent concern, not a primitive-level one):
 *
 *   `active`   — full block is expanded: number badge, title, optional
 *                subtitle, and children (the actual input fields).
 *   `answered` — collapsed summary line: number badge, title, provided
 *                `summary` node, and an "Ändern" text link that fires
 *                `onEdit`. Vertical rhythm collapses to a single row so
 *                the sequence of answered blocks reads as an audit
 *                trail above the currently-active block.
 *
 * ═══ VERTICAL RHYTHM (v3.1 §5.1 line 244) ═══════════════════════════
 *
 * Between numbered blocks the parent applies `space-y-8` (32 px). This
 * primitive does NOT wrap itself in that margin — the parent's
 * `<div className="space-y-8">` is authoritative. The primitive owns
 * the INTERNAL rhythm only (title-to-subtitle `space-y-1.5`,
 * subtitle-to-content `space-y-4`).
 *
 * ═══ ARIA ══════════════════════════════════════════════════════════
 *
 * The number badge is `aria-hidden` (decorative — it's the visible
 * ordinal, the semantic order is DOM order). The title uses a native
 * `<h2>` so screen readers get a heading landmark per block. The
 * answered-state Ändern link is a `<button type="button">` so keyboard
 * users can activate it without navigating.
 */

export type SchrittBlockState = 'active' | 'answered';

export interface SchrittBlockProps {
  readonly n: number;
  readonly title: string;
  readonly subtitle?: string;
  readonly state: SchrittBlockState;
  /** Shown ONLY in `answered` state, to the right of the title. */
  readonly summary?: ReactNode;
  /** Fires when the "Ändern" text link is clicked (answered state only). */
  readonly onEdit?: () => void;
  /** The input content — shown ONLY in `active` state. */
  readonly children?: ReactNode;
  /**
   * Sebastian ruling 2026-09-24 — hide the "{n}." ordinal for the
   * personen block. The new one-question-at-a-time flow answers the
   * question through affordances (the tap buttons and compact rows),
   * not through a numbered heading, so the leading "1." reads as
   * clutter. Blocks 2 (Freitext) + 3 (Titelwunsch) keep their
   * numbers — they are still numbered-block progressive reveals
   * per v3.1 §6.
   */
  readonly hideNumber?: boolean;
}

export function SchrittBlock({
  n,
  title,
  subtitle,
  state,
  summary,
  onEdit,
  children,
  hideNumber = false,
}: SchrittBlockProps) {
  if (state === 'answered') {
    return (
      <div className="flex items-baseline gap-3 py-2 text-sm text-dream-navy">
        {!hideNumber && (
          <span aria-hidden className="font-semibold text-dream-navy/70">
            {n}.
          </span>
        )}
        <h2 className="font-semibold">{title}</h2>
        {summary !== undefined && <span className="flex-1 text-dream-navy/70">{summary}</span>}
        {onEdit !== undefined && (
          <button
            type="button"
            onClick={onEdit}
            className="text-sm text-dream-navy underline focus-visible:outline-2 focus-visible:outline-dream-navy/50"
          >
            {STRINGS.book.personen.blockEditLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-baseline gap-3">
          {!hideNumber && (
            <span aria-hidden className="font-semibold text-dream-navy/70">
              {n}.
            </span>
          )}
          <h2 className="text-lg font-semibold text-dream-navy">{title}</h2>
        </div>
        {subtitle !== undefined && (
          <p
            className={
              hideNumber ? 'text-sm text-dream-navy/70' : 'pl-6 text-sm text-dream-navy/70'
            }
          >
            {subtitle}
          </p>
        )}
      </div>
      <div className={hideNumber ? '' : 'pl-6'}>{children}</div>
    </div>
  );
}
